import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiRequestError } from "../api/client.js";
import { authApi, describeLoginError, type AuthState } from "../api/auth.js";
import {
  Button,
  Card,
  Countdown,
  Eyebrow,
  Icons,
  PinInput,
  Status,
  useToast,
} from "../design/components/index.js";
import { VaultBrowser } from "../components/VaultBrowser.js";

/**
 * Einstieg in den geschuetzten Bereich.
 *
 * Die Seite zeigt ausschliesslich an, was der Server meldet. Sie entscheidet
 * nie selbst, ob ein Zugriff erlaubt ist -- die Pruefung sitzt vollstaendig im
 * Backend, und diese Ansicht wuerde einem Angreifer nichts nuetzen, das er
 * nicht ohnehin durch Ausprobieren erfaehrt.
 */
export function Vault(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const toast = useToast();

  const [state, setState] = useState<AuthState | null>(null);
  const [busy, setBusy] = useState(false);

  const loginError = params.get("fehler");

  const load = useCallback(async () => {
    try {
      setState(await authApi.state());
    } catch {
      setState(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Den Fehler aus der Adresszeile entfernen, sobald er angezeigt wurde --
  // sonst bleibt er beim Neuladen stehen und wirkt wie ein neuer Fehlschlag.
  useEffect(() => {
    if (!loginError) return;
    const timer = setTimeout(() => {
      params.delete("fehler");
      setParams(params, { replace: true });
    }, 100);
    return () => clearTimeout(timer);
  }, [loginError, params, setParams]);

  const login = useCallback(async () => {
    setBusy(true);
    try {
      const { redirectUrl } = await authApi.beginLogin("/vault");
      window.location.assign(redirectUrl);
    } catch (error) {
      setBusy(false);
      toast.show(
        error instanceof ApiRequestError ? error.message : "Anmeldung nicht möglich.",
        "error",
      );
    }
  }, [toast]);

  const logout = useCallback(async () => {
    await authApi.logout();
    await load();
    toast.show("Abgemeldet.", "success");
  }, [load, toast]);

  return (
    <div className="container section">
      <Eyebrow>Vault</Eyebrow>
      <h1 style={{ marginTop: "var(--space-2)" }}>Geschützter Bereich.</h1>

      {loginError ? (
        <div
          role="alert"
          style={{
            marginTop: "var(--space-4)",
            padding: "var(--space-2) var(--space-3)",
            borderRadius: "var(--radius-consent)",
            border: "1.5px solid var(--color-danger)",
            color: "var(--color-danger)",
            maxWidth: "40rem",
          }}
        >
          {describeLoginError(loginError)}
        </div>
      ) : null}

      <div
        style={{
          marginTop: "var(--space-6)",
          // Der entsperrte Bereich zeigt einen Datei-Browser und braucht mehr
          // Breite als die schmalen Anmeldekarten davor.
          maxWidth: state?.level === "vault_totp_verified" ? "56rem" : "40rem",
        }}
      >
        {state === null ? (
          <p style={{ color: "var(--color-granite)" }}>Lädt…</p>
        ) : !state.vaultConfigured ? (
          <NotConfigured />
        ) : !state.authenticated ? (
          <LoginPrompt onLogin={login} busy={busy} />
        ) : !state.isOwner ? (
          <NotOwner name={state.displayName} onLogout={logout} />
        ) : state.level !== "vault_totp_verified" ? (
          <TotpStep name={state.displayName} onVerified={setState} onLogout={logout} />
        ) : (
          <Unlocked
            name={state.displayName}
            expiresAt={state.vaultExpiresAt}
            onLogout={logout}
          />
        )}
      </div>
    </div>
  );
}

function NotConfigured(): React.ReactElement {
  return (
    <Card variant="raised">
      <Status kind="waiting">Nicht eingerichtet</Status>
      <p style={{ marginTop: "var(--space-3)", color: "var(--color-granite)" }}>
        Auf diesem Server ist keine Anmeldung konfiguriert. Der geschützte Bereich bleibt
        deshalb geschlossen.
      </p>
      <p style={{ marginTop: "var(--space-2)", color: "var(--color-granite)" }}>
        Setze <code>OIDC_ISSUER</code>, <code>OIDC_CLIENT_ID</code> und{" "}
        <code>OIDC_CLIENT_SECRET_FILE</code> sowie ein Eigentümermerkmal.
      </p>
    </Card>
  );
}

function LoginPrompt({
  onLogin,
  busy,
}: {
  onLogin: () => void;
  busy: boolean;
}): React.ReactElement {
  return (
    <Card variant="raised">
      <Icons.Lock size={28} />
      <h2 style={{ marginTop: "var(--space-2)", fontSize: "var(--text-h3)" }}>Anmeldung nötig</h2>
      <p style={{ marginTop: "var(--space-2)", color: "var(--color-granite)" }}>
        Der Zugriff auf Nextcloud, Paperless und den Dateispeicher setzt zwei Schritte voraus:
        die Anmeldung bei Authentik und anschließend einen Code aus deiner Authenticator-App.
      </p>
      <div style={{ marginTop: "var(--space-4)" }}>
        <Button onClick={onLogin} disabled={busy}>
          {busy ? "Weiterleitung…" : "Bei Authentik anmelden"}
        </Button>
      </div>
    </Card>
  );
}

function NotOwner({
  name,
  onLogout,
}: {
  name: string | null;
  onLogout: () => void;
}): React.ReactElement {
  return (
    <Card variant="raised">
      <Status kind="error">Kein Zugriff</Status>
      <p style={{ marginTop: "var(--space-3)" }}>
        {name ? <strong>{name}</strong> : "Dieses Konto"} hat keinen Zugriff auf den geschützten
        Bereich.
      </p>
      <div style={{ marginTop: "var(--space-4)" }}>
        <Button variant="secondary" onClick={onLogout}>
          Abmelden
        </Button>
      </div>
    </Card>
  );
}

/**
 * Zweiter Faktor.
 *
 * Der Code wird abgeschickt, sobald die sechste Ziffer steht -- ein
 * zusaetzlicher Knopf waere ein ueberfluessiger Schritt, weil die Laenge
 * eindeutig ist.
 */
function TotpStep({
  name,
  onVerified,
  onLogout,
}: {
  name: string | null;
  onVerified: (state: AuthState) => void;
  onLogout: () => void;
}): React.ReactElement {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (value: string) => {
      if (busy) return;
      setBusy(true);
      try {
        onVerified(await authApi.verifyTotp(value));
      } catch (caught) {
        setError(
          caught instanceof ApiRequestError ? caught.message : "Der Code konnte nicht geprüft werden.",
        );
        // Zurücksetzen für den nächsten Versuch. Direkt und nicht über
        // onChange -- sonst löscht dessen Handler den soeben gesetzten Fehler.
        setCode("");
      } finally {
        setBusy(false);
      }
    },
    [busy, onVerified],
  );

  return (
    <Card variant="raised">
      <Status kind="waiting">Angemeldet · zweiter Schritt</Status>
      <h2 style={{ marginTop: "var(--space-3)", fontSize: "var(--text-h3)" }}>
        {name ?? "Angemeldet"}
      </h2>
      <p style={{ marginTop: "var(--space-2)", color: "var(--color-granite)" }}>
        Gib den sechsstelligen Code aus deiner Authenticator-App ein.
      </p>

      <div style={{ marginTop: "var(--space-4)" }}>
        <PinInput
          length={6}
          value={code}
          onChange={(next) => {
            setCode(next);
            setError(undefined);
          }}
          onComplete={submit}
          label="Sechsstelliger Code aus der Authenticator-App"
          error={error}
          disabled={busy}
          autoFocus
        />
      </div>

      <div style={{ marginTop: "var(--space-2)" }}>
        <Button variant="secondary" onClick={onLogout}>
          Abmelden
        </Button>
      </div>
    </Card>
  );
}

function Unlocked({
  name,
  expiresAt,
  onLogout,
}: {
  name: string | null;
  expiresAt: string | null;
  onLogout: () => void;
}): React.ReactElement {
  return (
    <Card variant="raised">
      <Status kind="connected">Entsperrt</Status>
      <h2 style={{ marginTop: "var(--space-3)", fontSize: "var(--text-h3)" }}>
        {name ?? "Willkommen"}
      </h2>

      {expiresAt ? (
        <div style={{ marginTop: "var(--space-2)" }}>
          <Countdown until={expiresAt} label="Ohne Aktivität gesperrt in" />
        </div>
      ) : null}

      <div style={{ marginTop: "var(--space-6)" }}>
        <VaultBrowser />
      </div>

      <div style={{ marginTop: "var(--space-6)" }}>
        <Button variant="secondary" onClick={onLogout}>
          Abmelden
        </Button>
      </div>
    </Card>
  );
}
